import { MutableRefObject, useEffect, useRef } from "react";

export type HandlerFn = (arg0: React.SyntheticEvent<any>) => void;

export interface Element {
  addEventListener: (eventName: string, arg1: HandlerFn) => void;
  removeEventListener: (eventName: string, arg1: HandlerFn) => void;
}

export default function useEventListener(
  eventName: string,
  handler: HandlerFn,
  element?:
    | MutableRefObject<Element | HTMLElement | undefined>
    | undefined
    | null
    | HTMLElement
    | Element,
  options?: boolean | AddEventListenerOptions
) {
  const savedHandler = useRef<HandlerFn>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const actualElement =
      element && "current" in element ? element.current : element;
    const isSupported = actualElement && actualElement.addEventListener;

    if (!isSupported) {
      return;
    }

    // Defining this function is necessary for functionality, see README for reasoning and tests for behaviour
    const eventListener = (event: React.SyntheticEvent<any>) => {
      if (!savedHandler.current) {
        throw new Error(
          `The handler argument passed to useEventListener is not defined. This cannot be called by the event listener.`
        );
      }

      return savedHandler.current(event);
    };

    // @ts-ignore - element will be defined
    actualElement.addEventListener(eventName, eventListener, options);

    return () => {
      // @ts-ignore - element will be defined
      actualElement.removeEventListener(eventName, eventListener, options);
    };
  }, [eventName, element, options]);
}
